/**
 * Question Repository
 * Extends BaseRepository for product questions and answers
 */

import { ProductQuestion, QuestionVote } from '../domain/types';
import { COLLECTIONS } from '../domain/collections';
import {
    BaseRepository,
    BaseEntity
} from '../domain/services/BaseRepository';
import {
    collection,
    query,
    where,
    orderBy,
    limit as firestoreLimit,
    getDocs,
    getDoc,
    doc as firestoreDoc,
    updateDoc,
    addDoc,
    deleteDoc,
    increment,
    writeBatch,
    QueryDocumentSnapshot,
    DocumentData,
    DocumentSnapshot
} from 'firebase/firestore';
import { db } from '../infrastructure/firebaseConfig';
import { logger } from '../infrastructure/logger';
import { CacheInvalidator } from '../utils/cacheInvalidation';

export interface QuestionEntity extends BaseEntity {
    productId: string;
    shopId: string;
    userId: string;
    userName: string;
    question: string;
    answer?: string;
    answeredBy?: string;
    answeredAt?: string;
    helpful: number;
    notHelpful: number;
    status: 'pending' | 'answered' | 'hidden';
    hideFromPublic: boolean;
}

export interface QuestionVoteEntity extends BaseEntity {
    questionId: string;
    userId: string;
    voteType: 'helpful' | 'not_helpful';
}

class QuestionRepository extends BaseRepository<QuestionEntity> {
    protected collectionName = COLLECTIONS.QUESTIONS;
    private votesColl = 'questionVotes';

    protected toEntity(doc: QueryDocumentSnapshot<DocumentData>): QuestionEntity {
        const data = doc.data();
        return {
            id: doc.id,
            productId: (data.productId as string) || '',
            shopId: (data.shopId as string) || '',
            userId: (data.userId as string) || '',
            userName: (data.userName as string) || '',
            question: (data.question as string) || '',
            answer: data.answer as string | undefined,
            answeredBy: data.answeredBy as string | undefined,
            answeredAt: data.answeredAt as string | undefined,
            helpful: (data.helpful as number) || 0,
            notHelpful: (data.notHelpful as number) || 0,
            status: (data.status as QuestionEntity['status']) || 'pending',
            hideFromPublic: !!data.hideFromPublic,
            createdAt: (data.createdAt as string) || new Date().toISOString(),
            updatedAt: (data.updatedAt as string) || new Date().toISOString()
        };
    }

    protected toDocument(data: Partial<QuestionEntity>): DocumentData {
        const { id, createdAt, ...rest } = data as Record<string, unknown>;
        void id;
        void createdAt;
        return rest as DocumentData;
    }

    async getQuestionById(id: string): Promise<QuestionEntity | undefined> {
        try {
            const docRef = firestoreDoc(this.collection, id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                return { id: docSnap.id, ...docSnap.data() } as QuestionEntity;
            }
            return undefined;
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching question ${id}`, error);
            return undefined;
        }
    }

    async getQuestionsByProduct(productId: string, limitCount: number = 50): Promise<QuestionEntity[]> {
        try {
            const q = query(
                this.collection,
                where('productId', '==', productId),
                where('status', 'in', ['pending', 'answered']),
                where('hideFromPublic', '==', false),
                orderBy('createdAt', 'desc'),
                firestoreLimit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching questions for product ${productId}`, error);
            return [];
        }
    }

    async getQuestionsByShop(shopId: string): Promise<QuestionEntity[]> {
        try {
            const q = query(
                this.collection,
                where('shopId', '==', shopId),
                orderBy('createdAt', 'desc'),
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching questions for shop ${shopId}`, error);
            return [];
        }
    }

    async getPendingQuestionsByShop(shopId: string): Promise<QuestionEntity[]> {
        try {
            const q = query(
                this.collection,
                where('shopId', '==', shopId),
                where('status', '==', 'pending'),
                orderBy('createdAt', 'asc'),
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching pending questions for shop ${shopId}`, error);
            return [];
        }
    }

    async getQuestionsByUser(userId: string): Promise<QuestionEntity[]> {
        try {
            const q = query(
                this.collection,
                where('userId', '==', userId),
                orderBy('createdAt', 'desc'),
                firestoreLimit(100)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching questions for user ${userId}`, error);
            return [];
        }
    }

    async createQuestion(question: Omit<QuestionEntity, 'id' | 'createdAt' | 'updatedAt' | 'helpful' | 'notHelpful' | 'status'>): Promise<string> {
        const docRef = await addDoc(this.collection, {
            ...question,
            helpful: 0,
            notHelpful: 0,
            status: 'pending',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        CacheInvalidator.invalidateProduct(question.productId);
        return docRef.id;
    }

    async answerQuestion(questionId: string, answer: string, partnerId: string): Promise<void> {
        try {
            const docRef = firestoreDoc(this.collection, questionId);
            await updateDoc(docRef, {
                answer,
                answeredBy: partnerId,
                answeredAt: new Date().toISOString(),
                status: 'answered',
                updatedAt: new Date().toISOString()
            });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[QuestionRepository] Error answering question ${questionId}`, error);
            throw error;
        }
    }

    async hideQuestion(questionId: string): Promise<void> {
        try {
            const docRef = firestoreDoc(this.collection, questionId);
            await updateDoc(docRef, {
                status: 'hidden',
                hideFromPublic: true,
                updatedAt: new Date().toISOString()
            });
            CacheInvalidator.invalidateAll();
        } catch (error) {
            logger.error(`[QuestionRepository] Error hiding question ${questionId}`, error);
            throw error;
        }
    }

    async voteHelpful(questionId: string, userId: string): Promise<boolean> {
        return this.voteQuestion(questionId, userId, 'helpful');
    }

    async voteNotHelpful(questionId: string, userId: string): Promise<boolean> {
        return this.voteQuestion(questionId, userId, 'not_helpful');
    }

    private async voteQuestion(questionId: string, userId: string, voteType: 'helpful' | 'not_helpful'): Promise<boolean> {
        try {
            const existingVote = await this.getUserVote(questionId, userId);
            if (existingVote) {
                if (existingVote.voteType === voteType) {
                    return false;
                }
                const batch = writeBatch(db);
                const voteRef = firestoreDoc(collection(db, this.votesColl), existingVote.id);
                batch.delete(voteRef);

                const questionRef = firestoreDoc(this.collection, questionId);
                const incrementValue = voteType === 'helpful' ? 1 : -1;
                const decrementValue = voteType === 'helpful' ? -1 : 1;
                batch.update(questionRef, {
                    helpful: increment(incrementValue),
                    notHelpful: increment(decrementValue),
                    updatedAt: new Date().toISOString()
                });
                await batch.commit();
            } else {
                await addDoc(collection(db, this.votesColl), {
                    questionId,
                    userId,
                    voteType,
                    createdAt: new Date().toISOString()
                });

                const questionRef = firestoreDoc(this.collection, questionId);
                const incrementField = voteType === 'helpful' ? 'helpful' : 'notHelpful';
                await updateDoc(questionRef, {
                    [incrementField]: increment(1),
                    updatedAt: new Date().toISOString()
                });
            }
            CacheInvalidator.invalidateAll();
            return true;
        } catch (error) {
            logger.error(`[QuestionRepository] Error voting on question ${questionId}`, error);
            return false;
        }
    }

    private async getUserVote(questionId: string, userId: string): Promise<QuestionVoteEntity | undefined> {
        try {
            const q = query(
                collection(db, this.votesColl),
                where('questionId', '==', questionId),
                where('userId', '==', userId),
                firestoreLimit(1)
            );
            const snapshot = await getDocs(q);
            if (!snapshot.empty) {
                const doc = snapshot.docs[0];
                return { id: doc.id, ...doc.data() } as QuestionVoteEntity;
            }
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching vote for question ${questionId}`, error);
        }
        return undefined;
    }

    async hasUserVoted(questionId: string, userId: string): Promise<boolean> {
        const vote = await this.getUserVote(questionId, userId);
        return !!vote;
    }

    async getQuestionStats(productId: string): Promise<{ total: number; answered: number; pending: number }> {
        try {
            const q = query(
                this.collection,
                where('productId', '==', productId),
                where('hideFromPublic', '==', false)
            );
            const snapshot = await getDocs(q);
            const questions = snapshot.docs.map(doc => doc.data() as QuestionEntity);
            return {
                total: questions.length,
                answered: questions.filter(q => q.status === 'answered').length,
                pending: questions.filter(q => q.status === 'pending').length
            };
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching question stats for product ${productId}`, error);
            return { total: 0, answered: 0, pending: 0 };
        }
    }

    async getAnsweredQuestionsByProduct(productId: string, limitCount: number = 20): Promise<QuestionEntity[]> {
        try {
            const q = query(
                this.collection,
                where('productId', '==', productId),
                where('status', '==', 'answered'),
                where('hideFromPublic', '==', false),
                orderBy('helpful', 'desc'),
                orderBy('createdAt', 'desc'),
                firestoreLimit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => this.toEntity(doc as QueryDocumentSnapshot<DocumentData>));
        } catch (error) {
            logger.error(`[QuestionRepository] Error fetching answered questions for product ${productId}`, error);
            return [];
        }
    }
}

export const questionRepository = new QuestionRepository();
